import React, { useState } from 'react';
import { auth } from '../lib/firebase';
import { createUserWithEmailAndPassword, signInWithEmailAndPassword, sendPasswordResetEmail, sendEmailVerification } from 'firebase/auth';
import { X, Mail, Lock, LogIn, UserPlus, RefreshCw } from 'lucide-react';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function AuthModal({ isOpen, onClose }: AuthModalProps) {
  const [isLoginMode, setIsLoginMode] = useState(true);
  const [isResetMode, setIsResetMode] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [nationalId, setNationalId] = useState('');
  const [dob, setDob] = useState('');
  
  const [needOtp, setNeedOtp] = useState(false);
  const [otp, setOtp] = useState('');
  const [patientId, setPatientId] = useState('');

  const [error, setError] = useState('');
  const [resetMsg, setResetMsg] = useState('');
  const [loading, setLoading] = useState(false);
  const [showResend, setShowResend] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setResetMsg('');
    setShowResend(false);
    setLoading(true);

    try {
      if (isResetMode) {
        if (!email) {
          setError('يرجى إدخال البريد الإلكتروني أولاً.');
          setLoading(false);
          return;
        }
        await sendPasswordResetEmail(auth, email);
        setResetMsg('تم إرسال رابط استعادة كلمة المرور بنجاح. يرجى تفقد بريدك الوارد.');
        setTimeout(() => setIsResetMode(false), 5000);
      } else if (isLoginMode) {
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        if (!userCredential.user.emailVerified) {
          await auth.signOut();
          setError('حسابك غير موثق. يرجى مراجعة بريدك الإلكتروني (بما في ذلك مجلد Spam).');
          setShowResend(true);
          setLoading(false);
          return;
        }
        onClose();
      } else {
        // REGISTRATION MODE
        if (!needOtp) {
          const nameParts = name.trim().split(/\s+/);
          if (nameParts.length < 3) {
            setError('يرجى إدخال الاسم الثلاثي كاملاً (يجب أن يحتوي على 3 مقاطع على الأقل).');
            setLoading(false);
            return;
          }

          const phoneRegex = /^07[0-9]{9}$/;
          if (!phoneRegex.test(phone)) {
            setError('يرجى إدخال رقم هاتف عراقي صحيح يتكون من 11 رقماً ويبدأ بـ 07.');
            setLoading(false);
            return;
          }

          const nationalIdRegex = /^\d{12}$/;
          if (!nationalIdRegex.test(nationalId)) {
            setError('يرجى إدخال رقم البطاقة الموحدة بشكل صحيح (يجب أن يتكون من 12 رقماً بالضبط).');
            setLoading(false);
            return;
          }

          if (password !== confirmPassword) {
            setError('كلمتا المرور غير متطابقتين.');
            setLoading(false);
            return;
          }
        }

        if (needOtp) {
          // CONFIRM REGISTRATION
          const res = await fetch("/api/patient/auth/confirm", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ patientId, otp, email, name, phone, nationalId, dob })
          });
          const data = await res.json();
          if (data.success) {
            const userCredential = await createUserWithEmailAndPassword(auth, email, password);
            await sendEmailVerification(userCredential.user);
            await auth.signOut();
            setResetMsg('تم التسجيل! يرجى مراجعة صندوق بريدك (وإذا لم تجده ابحث في صندوق الرسائل المزعجة Spam) والضغط على رابط التحقق قبل محاولة الدخول.');
            setShowResend(true);
            setNeedOtp(false);
            setIsLoginMode(true);
          } else {
            setError(data.error || "رمز التحقق غير صحيح");
          }
        } else {
          // CHECK REGISTRATION
          const checkRes = await fetch("/api/patient/auth/check", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ phone, nationalId, name, email })
          });
          const checkData = await checkRes.json();
          if (checkData.error) {
             setError(checkData.error);
             setLoading(false);
             return;
          }

          if (checkData.needOtp) {
            setNeedOtp(true);
            setPatientId(checkData.patientId);
            setResetMsg(checkData.message);
          } else {
            // New patient -> Confirm immediately
            try {
              const userCredential = await createUserWithEmailAndPassword(auth, email, password);
              const confirmRes = await fetch("/api/patient/auth/confirm", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email, name, phone, nationalId, dob, firebaseUid: userCredential.user.uid })
              });
              const confirmData = await confirmRes.json();
              if (confirmData.success) {
                await sendEmailVerification(userCredential.user);
                await auth.signOut();
                setResetMsg('تم التسجيل! يرجى مراجعة صندوق بريدك (وإذا لم تجده ابحث في صندوق الرسائل المزعجة Spam) والضغط على رابط التحقق قبل محاولة الدخول.');
                setShowResend(true);
                setIsLoginMode(true);
              } else {
                await userCredential.user.delete();
                setError(confirmData.error || "فشل التسجيل");
              }
            } catch (err: any) {
              if (err.code === 'auth/email-already-in-use') {
                setError('البريد الإلكتروني مسجل مسبقاً. الرجاء استخدام بريد آخر أو تسجيل الدخول.');
              } else {
                throw err;
              }
            }
          }
        }
      }
    } catch (err: any) {
      if (err.code === 'auth/email-already-in-use') {
        setError('البريد الإلكتروني مسجل مسبقاً.');
      } else if (err.code === 'auth/wrong-password' || err.code === 'auth/user-not-found' || err.code === 'auth/invalid-credential') {
        setError('البريد الإلكتروني أو كلمة المرور غير صحيحة.');
      } else if (err.code === 'auth/weak-password') {
        setError('كلمة المرور ضعيفة. يجب أن لا تقل عن 6 أحرف.');
      } else if (err.code === 'auth/invalid-email') {
        setError('صيغة البريد الإلكتروني غير صحيحة.');
      } else {
        setError('حدث خطأ أثناء العملية: ' + err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setLoading(true);
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      await sendEmailVerification(userCredential.user);
      await auth.signOut();
      setResetMsg('تم إرسال رابط التوثيق مجدداً! يرجى مراجعة البريد الوارد ومجلد الرسائل المزعجة (Spam).');
      setShowResend(false);
      setError('');
    } catch (err: any) {
      setError('فشل إرسال الرابط: ' + err.message);
    }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm" dir="rtl">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm flex flex-col max-h-[95vh] overflow-hidden animate-in zoom-in-95 duration-200">
        <div className="flex justify-between items-center p-4 border-b border-slate-100 bg-slate-50 shrink-0">
          <h2 className="font-black text-slate-800 text-lg flex items-center gap-2">
            {isResetMode ? <RefreshCw className="w-5 h-5 text-indigo-600" /> : isLoginMode ? <LogIn className="w-5 h-5 text-indigo-600" /> : <UserPlus className="w-5 h-5 text-indigo-600" />}
            {isResetMode ? 'استعادة كلمة المرور' : isLoginMode ? 'تسجيل الدخول' : 'إنشاء حساب جديد'}
          </h2>
          <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-rose-500 hover:bg-rose-50 rounded-lg transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4 overflow-y-auto">
          {error && (
            <div className="bg-rose-50 border border-rose-200 text-rose-700 p-3 rounded-xl text-xs font-bold text-center flex flex-col items-center gap-2">
              <span>{error}</span>
              {showResend && (
                <button
                  type="button"
                  onClick={handleResend}
                  className="px-3 py-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition font-semibold"
                >
                  إعادة إرسال رابط التوثيق
                </button>
              )}
            </div>
          )}
          
          {resetMsg && (
            <div className="bg-green-50 border border-green-200 text-green-700 p-3 rounded-xl text-xs font-bold text-center leading-relaxed flex flex-col items-center gap-2">
              <span>{resetMsg}</span>
              {showResend && (
                <button
                  type="button"
                  onClick={handleResend}
                  className="px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition font-semibold mt-1"
                >
                  إعادة إرسال في حال عدم الوصول
                </button>
              )}
            </div>
          )}

          {(!isResetMode && !isLoginMode && !needOtp) && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-600">الاسم الثلاثي</label>
                  <input
                    type="text"
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl py-2.5 px-4 text-sm outline-none focus:border-indigo-500 focus:bg-white"
                    placeholder="مثال: أحمد محمد علي"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-600">رقم الهاتف</label>
                  <input
                    type="tel"
                    required
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl py-2.5 px-4 text-sm outline-none focus:border-indigo-500 focus:bg-white text-left font-mono"
                    placeholder="07xxxxxxxxx"
                    dir="ltr"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-600">سنة الميلاد</label>
                  <input
                    type="number"
                    required
                    min="1900"
                    max={new Date().getFullYear()}
                    value={dob}
                    onChange={(e) => setDob(e.target.value)}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl py-2.5 px-4 text-sm outline-none focus:border-indigo-500 focus:bg-white text-left"
                    placeholder="1990"
                    dir="ltr"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-600">الرقم الوطني الموحد</label>
                  <input
                    type="text"
                    required
                    minLength={12}
                    maxLength={12}
                    value={nationalId}
                    onChange={(e) => setNationalId(e.target.value)}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl py-2.5 px-4 text-sm outline-none focus:border-indigo-500 focus:bg-white text-left font-mono"
                    placeholder="12 رقماً"
                    dir="ltr"
                  />
                </div>
              </div>
            </>
          )}

          {needOtp ? (
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-indigo-700">أدخل رمز التحقق (OTP)</label>
              <input
                type="text"
                required
                maxLength={6}
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                className="w-full bg-indigo-50 border border-indigo-200 rounded-xl py-3 px-4 text-center text-lg font-bold outline-none focus:border-indigo-500 focus:bg-white font-mono tracking-widest text-indigo-900"
                placeholder="xxxxxx"
                dir="ltr"
              />
            </div>
          ) : (
            <>
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-600">البريد الإلكتروني</label>
                <div className="relative">
                  <Mail className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl py-2.5 pr-10 pl-4 text-sm outline-none focus:border-indigo-500 focus:bg-white transition-colors text-left font-sans"
                    placeholder="example@email.com"
                    dir="ltr"
                  />
                </div>
              </div>

              {!isResetMode && (
                <div className="space-y-1.5">
                  <div className="flex justify-between items-center">
                    <label className="text-xs font-bold text-slate-600">كلمة المرور</label>
                    {isLoginMode && (
                      <button 
                        type="button"
                        onClick={() => {
                          setIsResetMode(true);
                          setError('');
                          setResetMsg('');
                        }}
                        className="text-[10px] text-indigo-600 font-bold hover:underline"
                      >
                        نسيت كلمة المرور؟
                      </button>
                    )}
                  </div>
                  <div className="relative">
                    <Lock className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="password"
                      required
                      minLength={6}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full bg-slate-50 border border-slate-200 rounded-xl py-2.5 pr-10 pl-4 text-sm outline-none focus:border-indigo-500 focus:bg-white transition-colors text-left font-sans"
                      placeholder="••••••••"
                      dir="ltr"
                    />
                  </div>
                </div>
              )}

              {(!isResetMode && !isLoginMode && !needOtp) && (
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-600">تأكيد كلمة المرور</label>
                  <div className="relative">
                    <Lock className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="password"
                      required
                      minLength={6}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="w-full bg-slate-50 border border-slate-200 rounded-xl py-2.5 pr-10 pl-4 text-sm outline-none focus:border-indigo-500 focus:bg-white transition-colors text-left font-sans"
                      placeholder="••••••••"
                      dir="ltr"
                    />
                  </div>
                </div>
              )}
            </>
          )}

          <button
            type="submit"
            disabled={loading || (needOtp && otp.trim().length !== 6)}
            className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-extrabold py-3 rounded-xl text-sm transition-colors shadow-sm disabled:opacity-50"
          >
            {loading ? 'جاري المعالجة...' : isResetMode ? 'إرسال رابط الاستعادة' : needOtp ? 'تأكيد الرمز وربط الحساب' : isLoginMode ? 'دخول' : 'إنشاء حساب'}
          </button>

          <div className="text-center pt-2">
            {isResetMode ? (
              <button
                type="button"
                onClick={() => {
                  setIsResetMode(false);
                  setError('');
                  setResetMsg('');
                }}
                className="text-xs text-slate-500 font-bold hover:underline"
              >
                العودة لتسجيل الدخول
              </button>
            ) : (
              <button
                type="button"
                onClick={() => {
                  if (needOtp) {
                    setNeedOtp(false);
                    setResetMsg('');
                    setError('');
                  } else {
                    setIsLoginMode(!isLoginMode);
                    setError('');
                    setResetMsg('');
                  }
                }}
                className="text-xs text-indigo-600 font-bold hover:underline"
              >
                {needOtp ? '← تراجع وتغيير البيانات' : isLoginMode ? 'لا تملك حساباً؟ أنشئ حساباً جديداً' : 'لديك حساب بالفعل؟ سجل دخولك'}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
